
import { useEffect } from "react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft } from "lucide-react";
import { ReCaptchaWrapper } from "./ReCaptchaWrapper";
import { AuthFormInputs } from "./AuthFormInputs";
import { AuthFormActions } from "./AuthFormActions";
import { useAuthForm } from "./useAuthForm"; 
import { supabase } from "@/integrations/supabase/client";
import { getAndClearDeepLink } from "@/utils/deepLinking";
import { useToast } from "@/components/ui/use-toast";

interface AuthFormProps {
  formType?: 'login' | 'signup';
  toggleFormType?: () => void;
}

export const AuthForm = ({ formType: externalFormType, toggleFormType: externalToggleFormType }: AuthFormProps) => {
  const {
    email,
    setEmail,
    password,
    setPassword,
    userType,
    setUserType,
    isSignUp,
    setIsSignUp,
    isLoading,
    setIsLoading,
    error,
    setError,
    handleToggleFormType,
    handleForgotPassword,
  } = useAuthForm(externalFormType, externalToggleFormType);
  const { toast } = useToast();

  // Handle external form type changes
  useEffect(() => {
    if (externalFormType) {
      setIsSignUp(externalFormType === 'signup');
    }
  }, [externalFormType, setIsSignUp]);

  const handleSubmit = async (e: React.FormEvent, reCaptchaToken: string) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      if (!email || !password) {
        throw new Error("Please fill in both email and password");
      }

      if (isSignUp) {
        const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
          email,
          password,
        });

        if (signUpError) throw signUpError;

        if (signUpData.user) {
          // Map the new account types to appropriate roles
          const getRoleFromAccountType = (accountType: string): "financial_analyst" | "compliance_manager" | "e_concierge" | "admin" => {
            switch (accountType) {
              case 'quant':
                return 'financial_analyst';
              case 'quant-pro':
                return 'financial_analyst';
              case 'enterprise':
                return 'compliance_manager';
              default:
                return 'financial_analyst';
            }
          };

          const { error: roleError } = await supabase
              .from('user_roles')
              .insert([{ user_id: signUpData.user.id, role: getRoleFromAccountType(userType) }]);

          if (roleError) {
              console.warn("Could not assign default role:", roleError.message);
          }

          toast({
              title: "Account created!",
              description: "Please check your email to verify your account.",
          });
        } else {
             if (signUpError?.message?.includes("User already registered")) {
                 setError("An account with this email already exists. Please sign in or reset your password.");
             } else {
                 setError("Sign up failed. Please try again.");
             }
        }
      } else {
        const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (signInError) throw signInError;

        if (signInData.user) {
          const deepLink = getAndClearDeepLink();
          window.location.href = deepLink || '/dashboard'; // Use direct window.location instead of navigate
          toast({
            title: "Welcome back!",
            description: "You've successfully signed in.",
          });
        }
      }
    } catch (error) {
      console.error("Authentication error details:", error);
      const errorMessage = error instanceof Error ? error.message : "An error occurred";
      setError(errorMessage);
      toast({
        title: "Authentication Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Determine Card Title and Description based on main app context
   const cardTitle = isSignUp ? "Create Account" : "Welcome Back";
   const cardDescription = isSignUp
     ? "Sign up to access the AI Trading Platform"
     : "Sign in to your Stewie Trader account";

  return (
    <ReCaptchaWrapper action="auth">
      {(token) => (
        <Card className="w-full max-w-md mx-auto bg-gray-800/90 border-gray-700 shadow-2xl backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="mt-4 text-white text-center">
              {cardTitle}
            </CardTitle>
            <CardDescription className="text-gray-400 text-center">
              {cardDescription}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={(e) => handleSubmit(e, token)} className="space-y-4">
              <AuthFormInputs
                emailValue={email}
                setEmailValue={setEmail}
                passwordValue={password}
                setPasswordValue={setPassword}
                userType={userType}
                setUserType={setUserType}
                isSignUp={isSignUp}
              />

              <AuthFormActions
                isLoading={isLoading}
                isSignUp={isSignUp}
                error={error}
                onForgotPassword={handleForgotPassword}
                onToggleFormType={handleToggleFormType}
              />
            </form>
          </CardContent>
          <CardFooter />
        </Card>
      )}
    </ReCaptchaWrapper>
  );
};
